import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import matchSorter from 'match-sorter';
import moment from 'moment-timezone';
import React from 'react';
import DynamicOverflow from 'react-dynamic-overflow';
import {
  Column,
  Filters,
  FilterProps as ReactTableFilterProps,
  Row,
  SortingRule,
  TableState,
  UseSortByState,
  useExpanded,
  useFilters,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import {useAlertPolicies} from 'app/components/Alerts/AlertPolicyProvider';
import {
  formatCompositeExpression,
  formatCompositeExpressionTruncated,
} from 'app/components/Alerts/alertPolicyUtils';
import {ApiFeature, SystemPaidImagery} from 'app/modules/Remote/Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser, getAreaUnit} from 'app/modules/Remote/Organization';
import EditTagsMenu from 'app/pages/MonitorProjectView/EditTagsMenu';
import {OrderedVisibleColumnConfiguration} from 'app/pages/PropertyOverview/PropertyTableColumnSettings';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import * as CONSTANTS from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import featureFlags from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import * as hookUtils from 'app/utils/hookUtils';
import * as immutableUtils from 'app/utils/immutableUtils';
import {
  getMultiFeaturePropertyParts,
  sortMultiFeaturePropertyPartsCallback,
} from 'app/utils/multiFeaturePropertyUtils';
import * as tagUtils from 'app/utils/tagUtils';
import * as userUtils from 'app/utils/userUtils';

import {
  AdditionalFeaturePropertyFilter,
  AlertCountFilter,
  AssigneeFilter,
  DateFilter,
  FilterProps,
  ImageryFilterValue,
  ImageryStatusFilter,
  NoteCountFilter,
  NoteCountFilterValue,
  TableTagFilter,
  decodeAlertCountFilterValue,
  makeTagsFilter,
} from './ColumnFilters';
import {ImageryNotificationsByFeatureId} from './PropertyOverview';
import cs from './PropertyOverview.module.scss';
import {HighResImageryCell, InputWrapper} from './PropertyTable';
import {getSelectedPropertyRows} from './PropertyTableSelection';
import {PropertyNameCell, PropertySelectionCell} from './PropertyTableSharedComponents';
import {ImageDetails, ImageryStatus, PropertyTableRow} from './PropertyTableTypes';
import useFiltersPatch from './useFiltersPatch';
import {makeAlertCountFilterPredicate, makeCustomColumn} from './utils';

import {NoteCountsByFeatureId} from '.';

/** Text filter that uses matchSorter's fuzzy matcher rather than the default contains. */
const fuzzyFilter = Object.assign(
  (rows: Row<PropertyTableRow>[], id: string, filterValue: string) => {
    const containsStringItems = matchSorter(rows, filterValue, {
      keys: [
        // Match if property name contains filterValue
        (row: Row<PropertyTableRow>) => row.values[id],
        // Also match if a row contains any subrows where the
        // multilocation property name contains filterValue
        (row: Row<PropertyTableRow>) =>
          row.original.features?.map((feature) =>
            feature?.getIn(['properties', 'multiFeaturePartName'])
          ),
      ],
      threshold: matchSorter.rankings.CONTAINS,
    });

    // Match if row id (name, for example) has acronym filterValue
    const acronymItems = matchSorter(rows, filterValue, {
      keys: [(row: Row<PropertyTableRow>) => row.values[id]],
      threshold: matchSorter.rankings.ACRONYM,
    });

    // Generate a set of ids to match against
    const filteredItems = new Set([...containsStringItems, ...acronymItems].map((i) => i.id));

    // Filter rows by ids in filteredItems
    return rows.filter((r) => filteredItems.has(r.id));
  },
  // Let the table remove the filter if the string is empty
  {autoRemove: (val: string) => !val}
);

// filter for custom columns
const additionalFeaturePropertyFilter = Object.assign(
  (
    rows: Row<PropertyTableRow>[],
    columnId: string,
    filterValue: unknown
  ): Row<PropertyTableRow>[] => {
    if (filterValue == null || typeof filterValue !== 'string' || filterValue === '') {
      return rows;
    }

    return rows.filter((r) => {
      const searchQuery = filterValue.toLowerCase();
      const flatRows = r.subRows?.length ? r.subRows : [r];
      return flatRows.some((subRow) => {
        const cell = subRow.values[columnId];
        const cellString = cell == null ? '' : String(cell).trim();
        switch (filterValue) {
          case 'any':
            return cellString.length > 0;
          case 'none':
            return cellString.length === 0;
          default:
            return cellString.toLowerCase().includes(searchQuery);
        }
      });
    });
  },
  {autoRemove: (val: unknown) => val == null || val === ''}
);

/**
 * "make" function so we can parameterize it over whether or not we're treating
 * hidden imagery as available.
 */
const makeImageryFilter =
  (isHiddenImageryShownAsAvailable: boolean) =>
  (
    rows: Row<PropertyTableRow>[],
    _,
    filterValue: ImageryFilterValue = 'all'
  ): Row<PropertyTableRow>[] => {
    if (filterValue === 'all') {
      return rows;
    } else {
      return rows.filter((r) => {
        const flatRows = r.subRows?.length ? r.subRows : [r];

        const imageryStatuses: ImageryStatus[] = flatRows.map((r) => r.values['imagery']);

        const getAvailableCount = (s: ImageryStatus) =>
          s.available.length + (isHiddenImageryShownAsAvailable ? s.hidden.length : 0);

        const getAvailableSubMeterCount = (s: ImageryStatus) =>
          s.availableSubMeter.length +
          (isHiddenImageryShownAsAvailable ? s.hiddenSubMeter.length : 0);

        const getPurchasedCount = (s: ImageryStatus) => s.purchased.length;
        const getPurchasedSubMeterCount = (s: ImageryStatus) => s.purchasedSubMeter.length;

        return (
          // If we are filtering for rows with purchased imagery, return rows
          // where some of its features have some purchased images.
          // If we are filtering for rows with no available or purchased imagery,
          // return rows where none of its features have available or purchased
          // images.
          (filterValue === 'purchased' && imageryStatuses.some((s) => getPurchasedCount(s))) ||
          // If we are filtering for rows with available but not purchased
          // imagery, return rows where some of its features have some available
          // images but no purchased images.
          (filterValue === 'not-purchased' &&
            imageryStatuses.some((s) => getAvailableCount(s) && !getPurchasedCount(s))) ||
          // If we are filtering for rows with available but not purchased sub-meter imagery,
          // return rows where some of its features have some sub-meter resolution images available
          // but no purchased sub-meter images.
          (filterValue === 'not-purchased-submeter' &&
            imageryStatuses.some(
              (s) => getAvailableSubMeterCount(s) && !getPurchasedSubMeterCount(s)
            )) ||
          (filterValue === 'none' &&
            imageryStatuses.some((s) => !getAvailableCount(s) && !getPurchasedCount(s)))
        );
      });
    }
  };

/**
 * Collection of our react-table filter types, to be used by columns.
 */
const FILTER_TYPES = {
  fuzzy: fuzzyFilter,
  additionalFeatureProperty: additionalFeaturePropertyFilter,
};

/**
 * Wrapper around react-table's useTable that configures it with the columns
 * and filters that we're using.
 */
export function usePropertyTable({
  year,
  groupedFeatures,
  imageryByFeatureId,
  userNoteCountsByFeatureId,
  alertNoteCountsByFeatureId,
  reportCountsByFeatureId,
  organizationUsers,
  organization,
  projectId,
  featureCollection,
  allowSelection,
  savedFilterState,
  savedSortState,
  savedSelectedRowIds,
  showImageryPurchaseDrawer,
  batchPatchFeature,
  openCustomizeTagsModal,
  isExpandedForTest,
  setYear,
  columnSettings,
  showFeatureIdInTitle,
  additionalFeatureProperties,
}: {
  projectId: string;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  year: number;
  groupedFeatures: I.ImmutableListOf<ApiFeature>[];
  imageryByFeatureId: ImageryNotificationsByFeatureId | null;
  userNoteCountsByFeatureId: hookUtils.StatusMaybe<NoteCountsByFeatureId>;
  alertNoteCountsByFeatureId: hookUtils.StatusMaybe<ApiAlertCountsByFeatureId>;
  reportCountsByFeatureId: Record<string, number> | undefined;
  organization: I.ImmutableOf<ApiOrganization>;
  organizationUsers: ApiOrganizationUser[];
  allowSelection: boolean;
  savedFilterState: Filters<I.ImmutableOf<ApiFeature[]>>;
  savedSortState: SortingRule<I.ImmutableListOf<ApiFeature>>[];
  savedSelectedRowIds: Record<string, boolean>;
  showImageryPurchaseDrawer: (
    features: I.ImmutableOf<ApiFeature[]>,
    activeFeatureId: number | null
  ) => void;
  batchPatchFeature: FeaturesActions['batchPatchFeature'];
  openCustomizeTagsModal: (() => void) | null;
  isExpandedForTest?: boolean;
  setYear: (year: number) => void;
  columnSettings: OrderedVisibleColumnConfiguration;
  showFeatureIdInTitle: boolean;
  additionalFeatureProperties: I.Set<string>;
}) {
  const featureCollectionId = featureCollection.get('id');
  const tagSettings = hookUtils.useContinuity(
    tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE),
    I.is
  );

  // If true, hidden imagery is treated as avaliable in the filter and display.
  const [isHiddenImageryShownAsAvailable, setIsHiddenImageryShownAsAvailable] =
    React.useState(false);

  const areaUnit = getAreaUnit(organization);

  let years = featureCollectionUtils.getProcessedImageryYears(featureCollection);
  years = hookUtils.useContinuity(years);

  const {alertPolicies} = useAlertPolicies();

  // Memoize area calculations by feature ID to avoid expensive recalculation
  const areaCache = React.useRef<Map<number, number>>(new Map());

  const getFeatureAreaM2Cached = React.useCallback((feature: I.ImmutableOf<ApiFeature>): number => {
    const featureId = feature.get('id');
    if (areaCache.current.has(featureId)) {
      return areaCache.current.get(featureId)!;
    }

    const area = featureUtils.getFeatureAreaM2(feature);
    areaCache.current.set(featureId, area);
    return area;
  }, []);

  // NOTE(eva): if adding a new column you want to be sortable/configurable,
  // also add it to getPropertyOverviewColumnMetadata.
  const columns = React.useMemo<Column<PropertyTableRow>[]>(() => {
    return [
      ...[
        {
          id: 'selection',
          className: cs.cellSelect,
          Header: ({flatRows, isAllRowsSelected, toggleAllRowsSelected, rows, toggleExpanded}) => {
            const expandableRows = rows.filter((r) => r.canExpand);

            const areAllRowsExpanded =
              expandableRows.length === rows.filter((r) => r.isExpanded).length;

            const icon = expandableRows.length
              ? areAllRowsExpanded
                ? 'collapse-all'
                : 'expand-all'
              : 'blank';

            return (
              <div
                className={cs.selectionInputWrapper}
                style={!allowSelection ? {visibility: 'hidden'} : {}}
                onClick={() => toggleAllRowsSelected()}
              >
                <B.Button
                  small
                  minimal
                  disabled={!allowSelection}
                  icon={icon}
                  style={{...(icon === 'blank' && {visibility: 'hidden'})}}
                  onClick={(e) => {
                    e.stopPropagation();
                    expandableRows.forEach((r) => {
                      toggleExpanded([r.id], areAllRowsExpanded ? false : true);
                    });
                  }}
                  title={areAllRowsExpanded ? 'Collapse all' : 'Expand all'}
                />

                <InputWrapper
                  onChange={() => toggleAllRowsSelected()}
                  disabled={!allowSelection}
                  state={
                    isAllRowsSelected
                      ? 'checked'
                      : getSelectedPropertyRows(flatRows).length
                        ? 'indeterminate'
                        : 'unchecked'
                  }
                />
              </div>
            );
          },
          Cell: ({row}) => {
            return (
              <PropertySelectionCell
                row={row}
                allowSelection={allowSelection}
                showExpandButton={true}
                className={cs.selectionInputWrapper}
              />
            );
          },
        },
      ],
      {
        id: 'name',
        Header: 'Name',
        Filter: () => null,
        filter: 'fuzzy',
        accessor: ({features}) => features.first().getIn(['properties', 'name']),
        Cell: ({row, rows}) => {
          return (
            <PropertyNameCell
              row={row}
              organizationId={organization.get('id')}
              projectId={projectId}
              rows={rows}
              showProcessingStatus={true}
              showFeatureIdInTitle={showFeatureIdInTitle}
            />
          );
        },
      },
      {
        id: 'tags',
        Header: 'Tags',
        Filter: (props: FilterProps) => <TableTagFilter {...props} tagSettings={tagSettings} />,
        filter: makeTagsFilter(tagSettings),
        accessor: ({features}) => tagUtils.getFeaturesTagIds(features),
        Cell: ({row, cell}) => {
          const tagIds: ReturnType<typeof tagUtils.getFeatureTagIds> = cell.value;

          const activeTags = tagSettings.filter((t) => tagIds.includes(t!.get('id'))).toList();

          const [showAddTagsBtn, setShowAddTagsBtn] = React.useState(false);

          const {features} = cell.row.original;

          const tagCounts = tagUtils.getFeaturesTagCounts(features);

          // Blank out if expanded since information is represented by sub-rows.
          if (row.isExpanded) {
            return null;
          }

          return (
            <DynamicOverflow
              list={({tabRef}) =>
                activeTags
                  .map((t) => (
                    <div ref={tabRef} key={t!.get('id')}>
                      <tagUtils.Tag
                        setting={t!}
                        indeterminate={tagCounts.get(t!.get('id')) !== features.size}
                        className={cs.cellTag}
                      />
                    </div>
                  ))
                  .toArray()
              }
            >
              {({visibleElements, overflowElements, containerRef}) => {
                return (
                  <div ref={containerRef}>
                    <EditTagsMenu
                      featureCollectionId={featureCollectionId}
                      tagSettings={tagSettings}
                      tagStatusMaps={tagUtils.getFeaturesTagStatusMaps(features)}
                      onChange={(tagStatusMap) =>
                        batchPatchFeature({
                          featureCollectionId: featureCollection.get('id'),
                          features,
                          changes: featureUtils.tagStatusMapToPropertiesChanges(tagStatusMap),
                        })
                      }
                      openCustomizeTagsModal={openCustomizeTagsModal}
                      onClose={() => setShowAddTagsBtn(false)}
                      kind={tagUtils.TAG_KIND.FEATURE}
                    >
                      {activeTags.size ? (
                        <B.Button minimal className={cs.cellTagsBtn}>
                          <div style={{width: 'inherit'}}>
                            <div className={cs.cellTags}>{visibleElements}</div>
                            <p className={cs.cellTagsMore}>
                              {overflowElements.length > 0 && (
                                <span>+ {overflowElements.length} more...</span>
                              )}
                            </p>
                          </div>
                        </B.Button>
                      ) : (
                        allowSelection && (
                          <B.Button
                            className={classnames(cs.cellAddTagsBtn, {[cs.show]: showAddTagsBtn})}
                            minimal
                            text={
                              <div className={cs.cellTags}>
                                <B.Tag minimal>Add Property Tags…</B.Tag>
                              </div>
                            }
                            onClick={() =>
                              setShowAddTagsBtn((prevShowAddTagsBtn) => !prevShowAddTagsBtn)
                            }
                          />
                        )
                      )}
                    </EditTagsMenu>
                  </div>
                );
              }}
            </DynamicOverflow>
          );
        },
      },
      {
        id: 'area',
        Header: areaUnit === 'areaHectare' ? 'Hectares' : 'Acres',
        className: cs.cellArea,
        accessor: ({features}) => features.reduce((acc, f) => acc! + getFeatureAreaM2Cached(f!), 0),
        Cell: ({row, cell}) => {
          // Blank out if expanded since information is represented by sub-rows.
          if (row.isExpanded) {
            return null;
          }
          // We use the same calculation as for imagery ordering so that the
          // numbers are consistent.
          return conversionUtils.numberWithCommas(
            featureUtils.featureM2ToArea(cell.value, {unit: areaUnit})
          );
        },
      },
      {
        id: 'imagery',
        Header: (
          <div className={cs.cellWithYearSelectHeader}>
            <div>High-Res</div>

            <B.HTMLSelect value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => (
                <option key={y} value={y}>
                  '{y.toString().slice(-2)}
                </option>
              ))}
            </B.HTMLSelect>
          </div>
        ),
        loading: imageryByFeatureId === null,
        Filter: (props: FilterProps) => (
          <ImageryStatusFilter
            {...props}
            isHiddenImageryShownAsAvailable={isHiddenImageryShownAsAvailable}
            setIsHiddenImageryShownAsAvailable={setIsHiddenImageryShownAsAvailable}
          />
        ),
        filter: makeImageryFilter(isHiddenImageryShownAsAvailable),
        className: cs.cellHighRes,
        accessor: imageryAccessor.bind(null, imageryByFeatureId, year, featureCollection),
        Cell: ({cell, row, rows}) => {
          // Blank out if expanded since information is represented by sub-rows.
          if (row.isExpanded) {
            return null;
          }

          const isSubRow = getIsSubRow(row);

          return (
            <HighResImageryCell
              cell={cell}
              isHiddenImageryShownAsAvailable={isHiddenImageryShownAsAvailable}
              showImageryPurchaseDrawer={() =>
                showImageryPurchaseDrawer(
                  // If sub-row, show all features associated with parent (property) row.
                  isSubRow ? getParentRow(row, rows).original.features : row.original.features,

                  // If sub-row, style the part represented by this row as active.
                  isSubRow ? row.original.features.first().get('id') : null
                )
              }
            />
          );
        },
      },
      {
        id: 'userNoteCount',
        Header: (
          <div className={cs.cellWithYearSelectHeader}>
            <div>Notes</div>

            <B.HTMLSelect value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => (
                <option key={y} value={y}>
                  '{y.toString().slice(-2)}
                </option>
              ))}
            </B.HTMLSelect>
          </div>
        ),
        loading: userNoteCountsByFeatureId.status == 'unknown',
        Filter: NoteCountFilter,
        filter: (rows: Row<PropertyTableRow>[], _, filterValue: NoteCountFilterValue = null) => {
          if (filterValue === null) {
            return rows;
          } else {
            return rows.filter((r) => {
              const flatRows = r.subRows?.length ? r.subRows : [r];

              const noteCounts = flatRows.map((r) => r.values.userNoteCount);

              return (
                (filterValue === '>0' && noteCounts.some((n) => n >= 1)) ||
                (filterValue === '0' && noteCounts.some((n) => n == 0))
              );
            });
          }
        },
        className: cs.cellNoteCount,
        accessor: ({features}) => {
          if (userNoteCountsByFeatureId.status == 'unknown' || !userNoteCountsByFeatureId.value) {
            // if not loaded, show empty cells
            return null;
          }

          const sum = features.reduce((acc, f) => {
            if (!f) {
              return acc;
            }
            const count = userNoteCountsByFeatureId.value.getIn([f.get('id'), year]) || 0;
            return acc + count;
          }, 0);
          return sum;
        },
        Cell: ({cell, row}) => {
          if (row.isExpanded) {
            // Blank out if expanded since information is represented by sub-rows.
            return null;
          }

          return cell.value;
        },
      },
      {
        id: 'alertNoteCount',
        Header: 'Lookouts',
        className: cs.cellAlerts,
        loading: alertNoteCountsByFeatureId.status == 'unknown',
        Filter: (props: FilterProps) => (
          <AlertCountFilter
            {...props}
            alertPolicies={alertPolicies}
            selectedFeatureCollection={featureCollection}
            parcelAlertsInPlan={featureFlags.PARCEL_CHANGE_ALERTS(organization)}
          />
        ),
        filter: (rows: Row<PropertyTableRow>[], _, filterValue: string) => {
          if (!filterValue) {
            return rows;
          }

          const matchesFilter = makeAlertCountFilterPredicate(
            decodeAlertCountFilterValue(filterValue),
            alertPolicies
          );

          // Return true if any sub row (multi location property) has matching alert policies
          return rows.filter((row) => {
            const flatRows = row.subRows?.length ? row.subRows : [row];
            return flatRows.some((r) =>
              matchesFilter(alertNoteCountsByFeatureId.value?.[r.id] || {})
            );
          });
        },
        accessor: ({features}) => {
          if (alertNoteCountsByFeatureId.status == 'unknown' || !alertNoteCountsByFeatureId.value) {
            // if not loaded, show empty cells
            return null;
          }

          const sum = features.reduce((acc = 0, f) => {
            if (!f) {
              return acc;
            }
            const count = alertNoteCountsByFeatureId.value[f.get('id')]?.['all'] || 0;
            return acc + count;
          }, 0);
          return sum;
        },
        Cell: ({cell, row}) => {
          if (row.isExpanded) {
            // Blank out if expanded since information is represented by sub-rows.
            return null;
          }

          const badge = (
            <span
              className={classnames(cs.alertCountBadge, {
                [cs.alertCountBadgeActive]: !!cell.value,
              })}
            >
              {!!cell.value && cell.value}
            </span>
          );

          // A composite policy's rule doesn't fit the count badge, so it's surfaced on hover —
          // the truncated column-cell variant of the same formatter the alert card and wizard use.
          const compositeTooltipLines = alertPolicies.flatMap((policy) => {
            if (policy.alertType !== CONSTANTS.CUSTOM_ALERT_TYPE_COMPOSITE) return [];
            const isActiveOnRow = row.original.features.some(
              (f) =>
                !!f &&
                policy.enrollments?.some(
                  (enrollment) =>
                    enrollment.featureId === f.get('id') && enrollment.status === 'active'
                )
            );
            if (!isActiveOnRow) return [];

            const fullRule = formatCompositeExpression(policy.sourceId, policy.properties);
            const truncatedRule = formatCompositeExpressionTruncated(
              policy.sourceId,
              policy.properties,
              60
            );
            return [
              <div key={policy.id} title={fullRule}>
                <strong>{policy.name}:</strong> {truncatedRule}
              </div>,
            ];
          });

          if (!compositeTooltipLines.length) {
            return badge;
          }

          return <B.Tooltip content={<div>{compositeTooltipLines}</div>}>{badge}</B.Tooltip>;
        },
      },
      {
        id: 'reportCount',
        Header: 'Reports',
        accessor: ({features}) => {
          if (!reportCountsByFeatureId) {
            // if not loaded, show empty cells
            return null;
          }
          const sum = features.reduce((acc, f) => {
            if (!f) {
              return acc || 0;
            }
            const count = reportCountsByFeatureId[f.get('id').toString()] || 0;
            return (acc || 0) + count;
          }, 0);
          return sum;
        },
        Cell: ({cell, row}) => {
          if (row.isExpanded) {
            // Blank out if expanded since information is represented by sub-rows.
            return null;
          }
          return cell.value;
        },
      },
      {
        id: 'assignee',
        Header: featureUtils.APP_PROPERTY_NAMES[CONSTANTS.APP_PROPERTY_ASSIGNEE_EMAIL],
        className: cs.cellAssignee,
        // We wrap AssigneeFilter so we can provide it organizationUsers.
        Filter: (props: ReactTableFilterProps<I.ImmutableOf<ApiFeature[]>>) => (
          <AssigneeFilter organizationUsers={organizationUsers} {...props} />
        ),
        filter: makeAssigneeFilter(organizationUsers),
        Cell: ({row, cell}) => {
          // Blank out if expanded since information is represented by sub-rows.
          if (row.isExpanded) {
            return null;
          }

          const names = cell.value.map((email) => {
            const user = organizationUsers.find((u) => u!.email === email);
            return user ? userUtils.getSuffixedUserName(user) : email;
          });

          if (names.length < 2) {
            return names.join('');
          }

          // The column is narrow, so past two names the rest are counted. Hovering lists
          // everyone, which is the only way to read a collapsed or truncated cell.
          const label =
            names.length > 2
              ? `${names.slice(0, 2).join(', ')} +${names.length - 2}`
              : names.join(', ');

          return (
            <B.Tooltip
              content={
                <>
                  {cell.value.map((email, i) => (
                    <div key={email}>{names[i]}</div>
                  ))}
                </>
              }
            >
              <span>{label}</span>
            </B.Tooltip>
          );
        },
        accessor: ({features}): string[] => {
          const assignees = features
            .flatMap((f) => featureUtils.getFeatureAssignees(f!))
            .toArray() as string[];

          // A multi-location row shows everyone assigned to any of its locations. Sorted so
          // that both the rendered order and the column sort are stable.
          return [...new Set(assignees)].sort();
        },
      },
      makeDateRangeColumn({
        id: 'reportingDueDate',
        appProperty: CONSTANTS.APP_PROPERTY_REPORTING_DUE_DATE,
      }),
      ...(columnSettings?.orderedVisibleColumns ?? [])
        .filter((propertyName) => additionalFeatureProperties.has(propertyName))
        .map((propertyName) => ({
          ...makeCustomColumn(propertyName),
          Filter: (props: FilterProps) => (
            <AdditionalFeaturePropertyFilter {...props} table={table} />
          ),
        })),
    ] as Column<PropertyTableRow>[];
  }, [
    imageryByFeatureId,
    userNoteCountsByFeatureId,
    alertNoteCountsByFeatureId,
    reportCountsByFeatureId,
    organization,
    organizationUsers,
    areaUnit,
    year,
    allowSelection,
    projectId,
    featureCollectionId,
    isHiddenImageryShownAsAvailable,
    showImageryPurchaseDrawer,
    tagSettings,
    batchPatchFeature,
    openCustomizeTagsModal,
    setYear,
    years,
    featureCollection,
    alertPolicies,
    getFeatureAreaM2Cached,
    additionalFeatureProperties,
    showFeatureIdInTitle,
    columnSettings?.orderedVisibleColumns,
  ]);

  // filtered and sorted columns based on column settings
  const filteredAndOrderedColumns = React.useMemo(() => {
    const isDefaultState = columnSettings?.orderedVisibleColumns?.length === 0;

    if (isDefaultState) {
      return columns;
    }

    // Always include 'selection' and 'name' columns first
    const selectionColumn = columns.find((col) => col.id === 'selection');
    const nameColumn = columns.find((col) => col.id === 'name');
    const otherColumns = columns.filter((col) => col.id !== 'selection' && col.id !== 'name');

    // Reorder columns based on columnSettings.orderedVisibleColumns array order
    const orderedColumns = columnSettings.orderedVisibleColumns
      .map((id) => otherColumns.find((col) => col.id === id))
      .filter(
        (col): col is Column<PropertyTableRow> =>
          !!col && !!col.id && !['selection', 'name'].includes(col.id)
      );

    // Combine: selection (always first), then name (always second), then ordered columns
    return [
      ...[selectionColumn!],
      ...[nameColumn!],
      ...orderedColumns,
    ] as Column<PropertyTableRow>[];
  }, [columns, columnSettings]);

  // Primary key is a comma-delimited string of sorted feature IDs, allowing for
  // unique IDs across multi-location grouped features rows and single feature
  // sub-rows, which all share the same non-unique feature name.
  const getRowId = React.useCallback(
    (row: PropertyTableRow) =>
      row.features
        .map((feature) => feature!.get('id'))
        .sort()
        .join(','),
    []
  );

  const data = React.useMemo<PropertyTableRow[]>(
    () =>
      groupedFeatures.map((features) => ({
        features,

        // Only set subRows if there are multiple features associated with the
        // row. This allows us to use the row's `canExpand` boolean property
        // directly.
        subRows:
          features.size > 1
            ? features
                // Sort sub-rows alphabetically by part name.
                .sort((a, b) => {
                  const partA = getMultiFeaturePropertyParts(features, a).find((p) => p.isActive)!;
                  const partB = getMultiFeaturePropertyParts(features, b).find((p) => p.isActive)!;
                  return sortMultiFeaturePropertyPartsCallback(partA, partB);
                })
                .map((feature) => ({features: I.List([feature!])}))
                .toArray()
            : undefined,
      })),
    [groupedFeatures]
  );

  const initialState: Partial<TableState<I.ImmutableListOf<ApiFeature>>> &
    UseSortByState<I.ImmutableListOf<ApiFeature>> = {
    filters: savedFilterState,
    selectedRowIds: savedSelectedRowIds,
    sortBy: savedSortState,
    ...(isExpandedForTest
      ? {
          expanded: data
            .filter((datum) => datum.subRows?.length)
            .reduce((acc, datum) => ({...acc, [getRowId(datum)]: true}), {}),
        }
      : {}),
  };

  const table = useTable(
    {
      columns: filteredAndOrderedColumns,
      data,
      getRowId,
      filterTypes: FILTER_TYPES,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      initialState,
      autoResetExpanded: false,
    },
    useFilters,
    useFiltersPatch,
    useSortBy,
    useRowSelect,
    useExpanded
  );

  return {table};
}

function makeGetDateRangeFilter(appProperty: featureUtils.AppProperty) {
  return function (rows: Row<PropertyTableRow>[], _, filterValue: any): Row<PropertyTableRow>[] {
    if (filterValue === null) {
      return rows;
    } else {
      return rows.filter((r) => {
        const flatRows = r.subRows?.length ? r.subRows : [r];

        const dates = flatRows.map((r) => r.values[appProperty]);

        return (
          (filterValue === '*' && dates.some((d) => d)) ||
          (filterValue === '' && dates.some((d) => !d)) ||
          dates.some((d) => {
            const date = moment.unix(d);

            const dateEnd = moment(date).endOf('day');
            const todayStart = moment().startOf('day');
            const filterDateEnd = moment()
              .add(filterValue - 1, 'days')
              .endOf('day');

            return dateEnd.isBetween(todayStart, filterDateEnd, null, '[]');
          })
        );
      });
    }
  };
}

function makeDateRangeColumn({
  id,
  appProperty,
}: {
  id: string;
  appProperty: featureUtils.AppProperty;
}) {
  return {
    id,
    Header: featureUtils.APP_PROPERTY_NAMES[appProperty],
    className: cs.cellDate,
    Filter: DateFilter,
    filter: makeGetDateRangeFilter(appProperty),
    accessor: ({features}: PropertyTableRow) => {
      const APP_PROPERTY_PATH = ['properties', CONSTANTS.APP_PROPERTIES_KEY, appProperty];

      const firstFeatureValue = features.first().getIn(APP_PROPERTY_PATH as any, '');

      const hasMultipleValues =
        features.size > 1 &&
        features.some((f) => !!f!.getIn(APP_PROPERTY_PATH as any)) &&
        !features.every((f) => f!.getIn(APP_PROPERTY_PATH as any) === firstFeatureValue);

      return hasMultipleValues
        ? 'Multiple dates'
        : firstFeatureValue
          ? moment(firstFeatureValue).unix()
          : firstFeatureValue;
    },
    Cell: ({row, cell: {value}}) => {
      if (row.isExpanded) {
        return null;
      }

      if (value === 'Multiple dates') {
        return <i>{value}</i>;
      }

      return value ? moment.unix(value).format('MM/DD/YYYY') : value;
    },
  };
}

function makeAssigneeFilter(organizationUsers: ApiOrganizationUser[]) {
  return (rows: Row<PropertyTableRow>[], _, filterValue: any): Row<PropertyTableRow>[] => {
    if (filterValue === null) return rows;

    const selectedUser = organizationUsers.find((u) => u.email === filterValue);
    const selectedUserDisplayName = selectedUser
      ? userUtils.getSuffixedUserName(selectedUser)
      : null;

    return rows.filter((r) => {
      const flatRows = r.subRows?.length ? r.subRows : [r];
      // One entry per location, each holding that location's (possibly empty) assignee list.
      const assigneesByLocation: string[][] = flatRows.map((r) => r.values.assignee);
      const assignees = assigneesByLocation.flat();

      const columnAssigneeMatchesSelectedUser = assignees.some((assigneeValue) => {
        const lowerAssignee = assigneeValue.toLowerCase();
        return (
          lowerAssignee === filterValue.toLowerCase() ||
          (selectedUserDisplayName && lowerAssignee === selectedUserDisplayName.toLowerCase())
        );
      });

      const columnAssigneeUserNotFound = assignees.some(
        (a) =>
          !!a &&
          !organizationUsers.some(
            (u) =>
              u.email.toLowerCase() === a.toLowerCase() || u.name.toLowerCase() === a.toLowerCase()
          )
      );

      return (
        // If we are filtering for rows with an assignee, return rows where some
        // of its features have at least one assignee.
        (filterValue === '*' && assignees.length > 0) ||
        // If we are filtering for rows without an assignee, return rows where
        // some of its features have nobody assigned.
        (filterValue === '' && assigneesByLocation.some((a) => a.length === 0)) ||
        // when filtering for "assigned to non-user", return rows where some of
        // its features have an assignee, but that assignee is not found in the organization users list
        (filterValue === 'non-user' && columnAssigneeUserNotFound) ||
        // or, finally, return rows where the filterValue matches any of the emails in the
        // assignee column (which is displayed in the table as names)
        columnAssigneeMatchesSelectedUser
      );
    });
  };
}

/**
 * Determines what imagery is available and purchased for the given features and
 * year. Used by the "High-Res" column.
 */
function imageryAccessor(
  imageryByFeatureId: ImageryNotificationsByFeatureId | null,
  year: number,
  featureCollection: I.ImmutableOf<ApiFeatureCollection>,
  row: PropertyTableRow
): ImageryStatus {
  const {features} = row;

  const allAvailableImageryDetails: I.List<ImageDetails> = features
    .map((f): I.Iterable<unknown, ImageDetails> => {
      const system = f!.getIn(['properties', '__system']);
      const scenes = system?.getIn(['paidImagery', 'scenes']);

      return (
        imageryByFeatureId
          ?.get(f!.get('id'))
          ?.filter(
            (rec) =>
              !scenes?.find((s) => {
                const orderedSourceId = s!.get(0);
                const orderedSensingTime = s!.get(1);

                return (
                  orderedSourceId === rec!.get('source_id') &&
                  moment(orderedSensingTime).isSame(rec!.get('date'))
                );
              }) &&
              featureCollectionUtils.sourceHasHighResTruecolor(
                featureCollection,
                rec!.get('source_id')
              )
          )
          .map((rec) => {
            const sourceDetails = featureUtils.getSourceDetails(
              rec!.get('source_id') as featureUtils.SourceDetailsId
            );
            return {
              date: rec!.get('date'),
              sourceId: rec!.get('source_id'),
              resolution: sourceDetails?.resolution,
              isHidden: featureUtils.isSourceDateHidden(
                f!,
                [rec!.get('source_id')],
                rec!.get('date')
              ),
            };
          }) ?? I.List()
      );
    })
    .flatten(true)
    .toList();

  const paidImageryDetails: I.List<ImageDetails> = features
    .map((f): I.Iterable<unknown, ImageDetails> => {
      const system = f!.getIn(['properties', '__system']);
      const paidImagery =
        (system && system.get('paidImagery')) ||
        immutableUtils.toImmutableMap<I.ImmutableFields<SystemPaidImagery>>({
          scenes: I.List([]),
          limit: 0,
        });

      return paidImagery
        .get('scenes')
        .filter((s) =>
          featureCollectionUtils.sourceHasHighResTruecolor(featureCollection, s!.get(0))
        )
        .map((s) => {
          const sourceDetails = featureUtils.getSourceDetails(
            s!.get(0) as featureUtils.SourceDetailsId
          );
          return {
            date: s!.get(1),
            sourceId: s!.get(0),
            resolution: sourceDetails?.resolution,
            isHidden: false,
          };
        });
    })
    .flatten(true)
    .toList();

  const paidImagery = filterAndDedupeImageryDetails(paidImageryDetails, year);

  const availableImagery = filterAndDedupeImageryDetails(
    allAvailableImageryDetails.filter((d) => !d!.isHidden),
    year
  );

  const hiddenImagery = filterAndDedupeImageryDetails(
    allAvailableImageryDetails.filter((d) => !!d!.isHidden),
    year
  );

  const paidImagerySubMeter = paidImagery.filter((d) => !!d!.resolution);
  const availableSubMeterImagery = availableImagery.filter(
    (d) => !!d!.resolution && d!.resolution < 1
  );
  const hiddenSubMeterImagery = hiddenImagery.filter((d) => !!d!.resolution && d!.resolution < 1);

  return {
    available: availableImagery.toArray(),
    availableSubMeter: availableSubMeterImagery.toArray(),
    purchased: paidImagery.toArray(),
    purchasedSubMeter: paidImagerySubMeter.toArray(),
    hidden: hiddenImagery.toArray(),
    hiddenSubMeter: hiddenSubMeterImagery.toArray(),
  };
}

function filterAndDedupeImageryDetails(
  paidImageryDates: I.Iterable<number, ImageDetails>,
  year: number
) {
  return paidImageryDates
    .map((details) =>
      immutableUtils.toImmutableMap({
        dateMoment: moment(details!.date).startOf('day'),
        sourceId: details!.sourceId,
      })
    )
    .filter((details) => details!.get('dateMoment').get('year') === year)
    .sortBy((details) => details!.get('dateMoment').unix())
    .map((details) => {
      const sourceId = details!.get('sourceId');
      const sourceDetails = sourceId
        ? featureUtils.getSourceDetails(sourceId as featureUtils.SourceDetailsId)
        : null;
      return immutableUtils.toImmutableMap<ImageDetails>({
        date: details!.get('dateMoment').format('l'),
        sourceId: sourceId,
        resolution: sourceDetails?.resolution,
        isHidden: false,
      });
    })
    .toOrderedSet()
    .map((details): ImageDetails => details!.toJS());
}

function getParentRow(row: Row<PropertyTableRow>, rows: Row<PropertyTableRow>[]) {
  if (row.depth === 0) {
    return row;
  }
  return rows.find((r) => r.depth === 0 && r.id.includes(row.id)) as Row<PropertyTableRow>;
}

function getIsSubRow(row: Row<PropertyTableRow>) {
  return row.depth > 0;
}
